import { z } from "zod";
import { useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";

const schema = z.object({
  website_url: z
    .string()
    .trim()
    .url("Enter a valid website URL")
    .or(z.string().regex(/^[a-z0-9]([a-z0-9-]*\.)+[a-z]{2,}$/i, "Enter a valid domain (e.g., nike.com)"))
    .transform((url) => {
      // Auto-add https:// if not present
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return 'https://' + url;
      }
      return url;
    }),
  email: z.string().trim().email("Enter a valid email address"),
  accepted_legal: z
    .boolean()
    .refine((v) => v === true, "You must agree to the Privacy Policy and Terms of Service"),
  marketing_opt_in: z.boolean().optional().default(false),
});

export type AuditFormValues = z.infer<typeof schema>;

type Props = {
  onSubmit: (values: AuditFormValues) => Promise<void> | void;
  isLoading: boolean;
  serverError?: string | null;
};

export function AuditForm({ onSubmit, isLoading, serverError }: Props) {
  const [values, setValues] = useState<AuditFormValues>({
    website_url: "",
    email: "",
    accepted_legal: false,
    marketing_opt_in: false,
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Partial<Record<keyof AuditFormValues, string>>>({});

  const validate = (next: AuditFormValues) => {
    const result = schema.safeParse(next);
    if (result.success) {
      setErrors({});
      return true;
    }
    const fieldErrors: Partial<Record<keyof AuditFormValues, string>> = {};
    for (const issue of result.error.issues) {
      const k = issue.path[0] as keyof AuditFormValues;
      if (!fieldErrors[k]) fieldErrors[k] = issue.message;
    }
    setErrors(fieldErrors);
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ website_url: true, email: true, accepted_legal: true });
    if (!validate(values)) return;
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-[480px] rounded-2xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface))] p-6 md:p-7 fade-in"
      noValidate
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="website_url" className="mb-2 block text-[14px] font-medium text-white">
            Website URL
          </label>
          <input
            id="website_url"
            type="url"
            required
            inputMode="url"
            placeholder="nike.com"
            className="input"
            value={values.website_url}
            aria-invalid={!!(touched.website_url && errors.website_url)}
            aria-describedby={errors.website_url ? "website_url-error" : undefined}
            onChange={(e) => {
              const next = { ...values, website_url: e.target.value };
              setValues(next);
              if (touched.website_url) validate(next);
            }}
            onBlur={() => {
              setTouched((t) => ({ ...t, website_url: true }));
              validate(values);
            }}
          />
          {touched.website_url && errors.website_url && (
            <p id="website_url-error" className="mt-2 text-[13px] text-[color:hsl(var(--destructive))]">
              {errors.website_url}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="mb-2 block text-[14px] font-medium text-white">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            placeholder="you@company.com"
            className="input"
            value={values.email}
            aria-invalid={!!(touched.email && errors.email)}
            aria-describedby={errors.email ? "email-error" : undefined}
            onChange={(e) => {
              const next = { ...values, email: e.target.value };
              setValues(next);
              if (touched.email) validate(next);
            }}
            onBlur={() => {
              setTouched((t) => ({ ...t, email: true }));
              validate(values);
            }}
          />
          {touched.email && errors.email && (
            <p id="email-error" className="mt-2 text-[13px] text-[color:hsl(var(--destructive))]">
              {errors.email}
            </p>
          )}
        </div>

        <div className="space-y-3 pt-1">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              id="accepted_legal"
              type="checkbox"
              className="mt-[3px] h-4 w-4 shrink-0 rounded border border-[color:hsl(var(--border))] bg-[color:hsl(var(--elevated))] accent-[color:hsl(var(--primary))]"
              checked={values.accepted_legal}
              aria-invalid={!!(touched.accepted_legal && errors.accepted_legal)}
              aria-describedby={errors.accepted_legal ? "accepted_legal-error" : undefined}
              onChange={(e) => {
                const next = { ...values, accepted_legal: e.target.checked };
                setValues(next);
                setTouched((t) => ({ ...t, accepted_legal: true }));
                validate(next);
              }}
            />
            <span className="text-[13px] leading-relaxed text-[color:hsl(var(--muted-foreground))]">
              I agree to the{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white underline underline-offset-2 hover:text-[color:hsl(var(--primary))]"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white underline underline-offset-2 hover:text-[color:hsl(var(--primary))]"
              >
                Terms of Service
              </a>
              .
            </span>
          </label>
          {touched.accepted_legal && errors.accepted_legal && (
            <p id="accepted_legal-error" className="text-[13px] text-[color:hsl(var(--destructive))]">
              {errors.accepted_legal}
            </p>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              id="marketing_opt_in"
              type="checkbox"
              className="mt-[3px] h-4 w-4 shrink-0 rounded border border-[color:hsl(var(--border))] bg-[color:hsl(var(--elevated))] accent-[color:hsl(var(--primary))]"
              checked={!!values.marketing_opt_in}
              onChange={(e) => {
                const next = { ...values, marketing_opt_in: e.target.checked };
                setValues(next);
              }}
            />
            <span className="text-[13px] leading-relaxed text-[color:hsl(var(--muted-foreground))]">
              Send me CRO insights and updates (optional).
            </span>
          </label>
        </div>

        <button
          type="submit"
          className="cta-primary cta-primary--block"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Analyzing your site…
            </>
          ) : (
            <>
              Get my free audit
              <ArrowRight size={16} />
            </>
          )}
        </button>

        <p className="text-center text-[12px] text-[color:hsl(var(--subtle))]">
          We'll never share your email. One-time audit, no spam.
        </p>

        {serverError && (
          <p className="text-center text-[13px] text-[color:hsl(var(--destructive))]" role="alert">
            {serverError}
          </p>
        )}
      </div>
    </form>
  );
}
